import { useState, useEffect } from "react";
import axios from "axios";
import { ShoppingBag, Clock, TrendingUp, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/apiConfig";

export default function ProductPage({ profile, onRefresh }) {
  const [products, setProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buyingId, setBuyingId] = useState(null);
  const [viewTab, setViewTab] = useState("products"); // products | active | completed

  useEffect(() => {
    fetchProducts();
    fetchPurchases();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products`);
      setProducts(data);
    } catch { }
  };

  const fetchPurchases = async () => {
    try {
      const { data } = await axios.get(`${API}/products/my-purchases`);
      setPurchases(data);
    } catch { }
  };

  const handleBuy = async (productId) => {
    setBuyingId(productId);
    try {
      const { data } = await axios.post(`${API}/products/${productId}/buy`, {});
      toast.success(data.message);
      fetchPurchases();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    } finally {
      setBuyingId(null);
    }
  };

  const purchasedIds = new Set(purchases.map(p => p.product_id));
  const activePurchases = purchases.filter(p => !p.is_matured);
  const completedPurchases = purchases.filter(p => p.is_matured);

  return (
    <div className="product-page-full" data-testid="product-page">
      <h1 className="page-title heading-serif">Products & Tasks</h1>

      {/* View Tabs */}
      <div className="wallet-tabs mx-4 mb-4" data-testid="product-view-tabs">
        {[
          { id: "products", label: "Products" },
          { id: "active", label: `Active (${activePurchases.length})` },
          { id: "completed", label: `Completed (${completedPurchases.length})` },
        ].map(t => (
          <button key={t.id} className={`wallet-tab ${viewTab === t.id ? "active" : ""}`}
            onClick={() => setViewTab(t.id)} data-testid={`product-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Active Tasks */}
      {viewTab === "active" && (
        <div className="px-4 mb-6" data-testid="active-tasks">
          {activePurchases.length === 0 ? (
            <div className="empty-state mt-8">
              <Clock size={40} className="text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No active tasks</p>
              <p className="text-slate-600 text-sm mt-1">Buy a product to start earning!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activePurchases.map((p, i) => (
                <PurchaseCard key={i} purchase={p} index={i} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Completed Tasks */}
      {viewTab === "completed" && (
        <div className="px-4 mb-6" data-testid="completed-tasks">
          {completedPurchases.length === 0 ? (
            <div className="empty-state mt-8">
              <Check size={40} className="text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No completed tasks yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {completedPurchases.map((p, i) => (
                <PurchaseCard key={i} purchase={p} index={i} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Products View */}
      {viewTab === "products" && (
        <div className="px-4">
          <h3 className="text-sm font-medium text-slate-400 mb-3">Available Products</h3>
        {products.length === 0 ? (
          <div className="empty-state mt-8">
            <ShoppingBag size={40} className="text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No products available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {products.map((product, i) => {
              const isPurchased = purchasedIds.has(product.product_id);
              return (
                <div key={i} className="product-card" data-testid={`product-${i}`}>
                  <div className="product-card-header">
                    <div className="product-icon">
                      <TrendingUp size={20} className="text-[#d4af37]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold text-base">{product.name}</h4>
                      <p className="text-xs text-slate-400">Maturity: {product.maturity_days} days</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#d4af37] font-bold text-lg">Rs.{product.price}</p>
                    </div>
                  </div>

                  <div className="product-details">
                    <div className="product-detail-item">
                      <Clock size={14} className="text-slate-400" />
                      <span className="text-slate-400 text-xs">{product.maturity_days} Days</span>
                    </div>
                    <div className="product-detail-item">
                      <TrendingUp size={14} className="text-green-400" />
                      <span className="text-green-400 text-xs">Rs.{product.daily_earning}/day</span>
                    </div>
                    <div className="product-detail-item">
                      <span className="text-xs text-[#d4af37]">Total: Rs.{product.total_profit}</span>
                    </div>
                  </div>

                  <button
                    className={`btn-gold mt-3 ${isPurchased ? "opacity-50" : ""}`}
                    disabled={isPurchased || buyingId === product.product_id}
                    onClick={() => handleBuy(product.product_id)}
                    data-testid={`buy-product-${i}`}
                  >
                    {buyingId === product.product_id ? (
                      <Loader2 size={18} className="animate-spin mx-auto" />
                    ) : isPurchased ? "Already Purchased" : "Buy Now"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        </div>
      )}
    </div>
  );
}

function PurchaseCard({ purchase: p, index: i }) {
  return (
    <div className="purchase-card" data-testid={`purchase-${i}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="text-white font-semibold">{p.product_name}</h4>
          <p className="text-xs text-slate-400">Invested: Rs.{p.price}</p>
        </div>
        <span className={`status-badge ${p.is_matured ? "matured" : "active"}`}>
          {p.is_matured ? "Completed" : "Active"}
        </span>
      </div>
      <div className="earnings-progress">
        <div className="flex justify-between text-xs mb-2">
          <span className="text-slate-400">Earnings</span>
          <span className="text-[#d4af37]">Rs.{p.current_earning} / Rs.{p.total_profit}</span>
        </div>
        <div className="progress-bar-bg">
          <div className="progress-bar-fill" style={{ width: `${Math.min((p.completed_days / p.maturity_days) * 100, 100)}%` }} />
        </div>
        <div className="flex justify-between text-xs mt-2">
          <span className="text-slate-500">Day {p.completed_days}/{p.maturity_days}</span>
          <span className="text-slate-500">Rs.{p.daily_earning}/day</span>
        </div>
      </div>
      <div className="daily-breakdown mt-3">
        {Array.from({ length: p.maturity_days }, (_, d) => (
          <div key={d} className={`day-chip ${d < p.completed_days ? "completed" : d === p.completed_days ? "current" : ""}`}>
            <span className="text-xs">Day {d + 1}</span>
            <span className="text-xs font-bold">Rs.{(d + 1) * p.daily_earning}</span>
            {d < p.completed_days && <Check size={12} className="text-green-400" />}
          </div>
        ))}
      </div>
    </div>
  );
}
